// components/company/tabs/CompanyReviews.jsx
import { useState, Fragment, useMemo, useEffect } from "react";
import { Field } from "../../ui/Field";
import {
  updateCompany,
  addDeviezenPermit,
  updateDeviezenPermit,
  addCompanyTaxYear,
  updateCompanyTaxYear,
  addScreening,
  updateScreening,
  updateCompanyReview,
  deleteCompanyReview,
  addCompanyReview,
  uploadCocReceipt,
  downloadCompanyDoc,
  getCompanyDocSignedUrl,
  openDocumentWithSignedUrl,
} from "../../../api";
import { useNavigate } from "react-router-dom";

export const CompanyReviews = ({ reviews, devPermits, taxYears, screenings, docs = [], company, companyId, meta, notify, refetch }) => {
  // -----------------------------
  // State (existing cards)
  // -----------------------------
  const [editReview, setEditReview] = useState(null);
  const [corp, setCorp] = useState({
    coc_extract_date: company?.coc_extract_date ? company.coc_extract_date.slice(0, 10) : "",
    coc_fee_paid_through: company?.coc_fee_paid_through ? company.coc_fee_paid_through.slice(0, 10) : "",
    business_license_no: company?.business_license_no || "",
    business_license_expires_on: company?.business_license_expires_on ? company.business_license_expires_on.slice(0, 10) : "",
    deviezen_status: company?.deviezen_status || "none",
  });
  const [taxInfo, setTaxInfo] = useState({
    crib_number: company?.crib_number || "",
  });
  const [permitForm, setPermitForm] = useState({
    id: null,
    permit_no: "",
    kind: "exemption",
    scope: "",
    issued_on: "",
    expires_on: "",
    document_id: "",
  });
  const [taxForm, setTaxForm] = useState({
    id: null,
    fiscal_year: "",
    fs_year_end: "",
    fs_approved_on: "",
    profit_tax_filed_on: "",
    profit_tax_method: "",
    turnover_tax_filed_through: "",
    payroll_tax_filed_through: "",
    arrears_flag: 0,
    notes: "",
  });
  const [screenForm, setScreenForm] = useState({
    id: null,
    kind: "pep",
    provider: "",
    result: "clear",
    checked_on: "",
    reference: "",
  });

  // -----------------------------
  // NEW: CoC receipt helpers
  // -----------------------------
  const [cocUploading, setCocUploading] = useState(false);
  const [lastCocReceipt, setLastCocReceipt] = useState(null);

  const navigate = useNavigate();

  // Find the latest “CoC Fee Receipt <YEAR>” doc (company-level)
  useEffect(() => {
    try {
      const r =
        (Array.isArray(docs) ? docs : [])
          .filter((d) => !d.person_id && /^CoC Fee Receipt/i.test(String(d.identifier || "")))
          .sort((a, b) => new Date(b.issued_on || 0) - new Date(a.issued_on || 0))[0] || null;
      setLastCocReceipt(r);
    } catch {
      setLastCocReceipt(null);
    }
  }, [docs]);

  // Next due = paid_through (backend sets it to Jan 1 of next year on upload)
  const nextCocDue = useMemo(() => {
    const through = company?.coc_fee_paid_through ? new Date(company.coc_fee_paid_through) : null;
    if (!through) return null;
    return through.toISOString().slice(0, 10);
  }, [company?.coc_fee_paid_through]);

  // Tiny “who paid” parser (we stored it in the document note)
  const whoPaid = useMemo(() => {
    const note = String(lastCocReceipt?.note || "").toLowerCase();
    if (!note) return null;
    if (note.includes("paid by: client")) return "Client";
    if (note.includes("paid by: us")) return "Us";
    return null;
  }, [lastCocReceipt]);

  // Due-status chip (Google-style)
  const cocChip = useMemo(() => {
    const dueISO = nextCocDue;
    if (!dueISO) return { label: "No payment on record", tone: "danger" };
    const due = new Date(dueISO);
    const today = new Date();
    const days = Math.floor((due - new Date(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000);

    if (days < 0) return { label: `Overdue ${Math.abs(days)}d`, tone: "danger" };
    if (days === 0) return { label: "Due today", tone: "warning" };
    if (days <= 7) return { label: `Due in ${days}d`, tone: "warning" };
    return { label: `Due ${dueISO}`, tone: "info" };
  }, [nextCocDue]);

  const handleCocUpload = async (file, notes = "", paidBy = "we") => {
    if (!file) return;
    try {
      setCocUploading(true);
      await uploadCocReceipt(companyId, file, { notes, paidBy });
      await refetch();
      notify("CoC receipt uploaded. Coverage updated.");
    } catch (e) {
      notify(e?.message || "Upload failed");
    } finally {
      setCocUploading(false);
    }
  };

  // -----------------------------
  // Existing computed helpers
  // -----------------------------
  const type = company?.type_local_foreign?.toLowerCase?.();
  const isIntlOrForeign = type === "international" || type === "foreign";

  const latestScreenings = useMemo(() => {
    const map = { pep: null, sanctions: null, adverse_media: null };
    screenings.forEach((s) => {
      const current = map[s.kind];
      if (!current || new Date(s.checked_on) > new Date(current.checked_on)) {
        map[s.kind] = s;
      }
    });
    return map;
  }, [screenings]);

  // -----------------------------
  // Save actions (existing)
  // -----------------------------
  const saveCorp = async (e) => {
    e.preventDefault();
    try {
      await updateCompany(companyId, corp);
      await refetch();
      notify("Corporate details saved");
    } catch (error) {
      notify(error.message);
    }
  };

  const saveTaxInfo = async (e) => {
    e.preventDefault();
    try {
      await updateCompany(companyId, taxInfo);
      await refetch();
      notify("Tax information saved");
    } catch (error) {
      notify(error.message);
    }
  };

  const resetPermitForm = () =>
    setPermitForm({
      id: null,
      permit_no: "",
      kind: "exemption",
      scope: "",
      issued_on: "",
      expires_on: "",
      document_id: "",
    });

  const savePermit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...permitForm, document_id: permitForm.document_id || null };
      if (permitForm.id) {
        await updateDeviezenPermit(companyId, permitForm.id, payload);
      } else {
        await addDeviezenPermit(companyId, payload);
      }
      resetPermitForm();
      await refetch();
      notify("Permit saved successfully");
    } catch (error) {
      notify(error.message);
    }
  };

  const resetTaxForm = () =>
    setTaxForm({
      id: null,
      fiscal_year: "",
      fs_year_end: "",
      fs_approved_on: "",
      profit_tax_filed_on: "",
      profit_tax_method: "",
      turnover_tax_filed_through: "",
      payroll_tax_filed_through: "",
      arrears_flag: 0,
      notes: "",
    });

  const saveTaxYear = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...taxForm };
      if (taxForm.id) {
        await updateCompanyTaxYear(companyId, taxForm.id, payload);
      } else {
        await addCompanyTaxYear(companyId, payload);
      }
      resetTaxForm();
      await refetch();
      notify("Tax year saved successfully");
    } catch (error) {
      notify(error.message);
    }
  };

  const resetScreenForm = () =>
    setScreenForm({
      id: null,
      kind: "pep",
      provider: "",
      result: "clear",
      checked_on: "",
      reference: "",
    });

  const saveScreening = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...screenForm };
      if (screenForm.id) {
        await updateScreening(companyId, screenForm.id, payload);
      } else {
        await addScreening(companyId, payload);
      }
      resetScreenForm();
      await refetch();
      notify("Screening saved successfully");
    } catch (error) {
      notify(error.message);
    }
  };

  const editPermit = (permit) => {
    setPermitForm({
      id: permit.id,
      permit_no: permit.permit_no || "",
      kind: permit.kind || "exemption",
      scope: permit.scope || "",
      issued_on: permit.issued_on ? permit.issued_on.slice(0, 10) : "",
      expires_on: permit.expires_on ? permit.expires_on.slice(0, 10) : "",
      document_id: permit.document_id || "",
    });
  };

  const editTaxYear = (taxYear) => {
    setTaxForm({
      id: taxYear.id,
      fiscal_year: taxYear.fiscal_year || "",
      fs_year_end: taxYear.fs_year_end ? taxYear.fs_year_end.slice(0, 10) : "",
      fs_approved_on: taxYear.fs_approved_on ? taxYear.fs_approved_on.slice(0, 10) : "",
      profit_tax_filed_on: taxYear.profit_tax_filed_on ? taxYear.profit_tax_filed_on.slice(0, 10) : "",
      profit_tax_method: taxYear.profit_tax_method || "",
      turnover_tax_filed_through: taxYear.turnover_tax_filed_through ? taxYear.turnover_tax_filed_through.slice(0, 10) : "",
      payroll_tax_filed_through: taxYear.payroll_tax_filed_through ? taxYear.payroll_tax_filed_through.slice(0, 10) : "",
      arrears_flag: taxYear.arrears_flag ? 1 : 0,
      notes: taxYear.notes || "",
    });
  };

  const editScreening = (screening) => {
    setScreenForm({
      id: screening.id,
      kind: screening.kind || "pep",
      provider: screening.provider || "",
      result: screening.result || "clear",
      checked_on: screening.checked_on ? screening.checked_on.slice(0, 10) : "",
      reference: screening.reference || "",
    });
  };

  const removeReview = async (reviewId) => {
    if (!confirm("Delete review?")) return;
    try {
      await deleteCompanyReview(companyId, reviewId);
      await refetch();
      notify("Review deleted successfully");
    } catch (error) {
      notify(error.message);
    }
  };

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <>
      {/* Reviews table (unchanged) */}
      <div className="card table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Next review</th>
              <th>Risk level</th>
              <th>Reviewer</th>
              <th>Comments</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {reviews.map((review) => {
              const editing = editReview && editReview.id === review.id;
              return (
                <Fragment key={`review-${review.id}`}>
                  <tr>
                    <td>{review.review_date?.slice(0, 10) || "—"}</td>
                    <td>{review.next_review_date?.slice(0, 10) || "—"}</td>
                    <td>{review.risk_level}</td>
                    <td>{review.reviewer}</td>
                    <td className="cell-ellipsis">{review.comments || "—"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {!editing && (
                        <>
                          <button type="button" onClick={() => navigate(`/reviews/${review.id}/edit`)}>
                            Edit
                          </button>
                          <button type="button" className="secondary" style={{ marginLeft: 4 }} onClick={() => removeReview(review.id)}>
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>

                  {editing && (
                    <tr className="inline-editor-row">
                      <td colSpan={6}>
                        <form
                          onSubmit={async (e) => {
                            e.preventDefault();
                            try {
                              await updateCompanyReview(companyId, editReview.id, {
                                review_date: editReview.review_date || null,
                                next_review_date: editReview.next_review_date || null,
                                risk_level: editReview.risk_level,
                                reviewer: editReview.reviewer,
                                comments: editReview.comments,
                              });
                              setEditReview(null);
                              await refetch();
                              notify("Review updated successfully");
                            } catch (error) {
                              notify(error.message);
                            }
                          }}>
                          <div className="inline-editor">
                            <label>
                              <span>Review Date</span>
                              <input type="date" value={editReview.review_date} onChange={(e) => setEditReview({ ...editReview, review_date: e.target.value })} />
                            </label>
                            <label>
                              <span>Next Review</span>
                              <input type="date" value={editReview.next_review_date} onChange={(e) => setEditReview({ ...editReview, next_review_date: e.target.value })} />
                            </label>
                            <label>
                              <span>Risk Level</span>
                              <select value={editReview.risk_level} onChange={(e) => setEditReview({ ...editReview, risk_level: e.target.value })}>
                                <option value="">—</option>
                                <option value="Low">Low</option>
                                <option value="Medium">Medium</option>
                                <option value="High">High</option>
                                <option value="Very High">Very High</option>
                              </select>
                            </label>
                            <label>
                              <span>Reviewer</span>
                              <input value={editReview.reviewer} onChange={(e) => setEditReview({ ...editReview, reviewer: e.target.value })} />
                            </label>
                            <label className="span-2">
                              <span>Comments</span>
                              <textarea value={editReview.comments} onChange={(e) => setEditReview({ ...editReview, comments: e.target.value })} rows={3} />
                            </label>
                          </div>
                          <div className="inline-editor__actions">
                            <button type="submit">Save</button>
                            <button type="button" className="secondary" onClick={() => setEditReview(null)}>
                              Cancel
                            </button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {reviews.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center">
                  No reviews
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Corporate & Licenses (existing) */}
      <form className="card form-section" onSubmit={saveCorp}>
        <h3>Corporate & Licenses</h3>
        <div className="form-grid">
          <Field label="CoC extract date">
            <input type="date" value={corp.coc_extract_date} onChange={(e) => setCorp({ ...corp, coc_extract_date: e.target.value })} />
          </Field>
          <Field label="CoC fee paid through">
            <input type="date" value={corp.coc_fee_paid_through} onChange={(e) => setCorp({ ...corp, coc_fee_paid_through: e.target.value })} />
          </Field>
          <Field label="Business license no">
            <input value={corp.business_license_no} onChange={(e) => setCorp({ ...corp, business_license_no: e.target.value })} />
          </Field>
          <Field label="Business license expiry">
            <input type="date" value={corp.business_license_expires_on} onChange={(e) => setCorp({ ...corp, business_license_expires_on: e.target.value })} />
          </Field>
          {isIntlOrForeign && (
            <Field label="Deviezen status">
              <select value={corp.deviezen_status} onChange={(e) => setCorp({ ...corp, deviezen_status: e.target.value })}>
                <option value="none">None</option>
                <option value="exemption">Exemption</option>
                <option value="permit">Permit</option>
              </select>
            </Field>
          )}
        </div>
        <div className="form-actions">
          <button type="submit">Save</button>
        </div>
      </form>

      {/* NEW: CoC Fee card (Google-style quick action) */}
      <div id="coc-fee-card" className="card form-section">
        <h3>CoC Fee</h3>

        <div className="chip-group" style={{ marginTop: -8, marginBottom: 8 }}>
          {cocChip?.label && (
            <span className={`chip ${cocChip.tone === "danger" ? "status-rejected" : cocChip.tone === "warning" ? "status-pending_gov_approval" : "status-suspended"}`}>
              {cocChip.label}
            </span>
          )}
          {!!whoPaid && <span className="chip">{`Who paid: ${whoPaid}`}</span>}
          {lastCocReceipt && (
            <button
              type="button"
              className="text-btn"
              style={{ marginLeft: 8 }}
              onClick={async () => {
                try {
                  await openDocumentWithSignedUrl(() => getCompanyDocSignedUrl(companyId, lastCocReceipt.id), {
                    notify,
                    fallbackDownload: () => downloadCompanyDoc(companyId, lastCocReceipt.id, { admin: true }),
                  });
                } catch (e) {
                  notify(e?.message || "Could not open receipt");
                }
              }}>
              Open last receipt
            </button>
          )}
        </div>

        <div className="form-grid">
          <Field label="Covered through">
            <input value={company?.coc_fee_paid_through?.slice(0, 10) || ""} readOnly />
          </Field>
          <Field label="Next due">
            <input value={nextCocDue || ""} readOnly />
          </Field>
          <Field label="Upload receipt">
            <label className="btn">
              {cocUploading ? "Uploading…" : "Choose file"}
              <input type="file" style={{ display: "none" }} onChange={(e) => handleCocUpload(e.target.files?.[0], "", "we")} disabled={cocUploading} />
            </label>
          </Field>
        </div>
      </div>

      {/* Deviezen permits (existing)… */}
      {isIntlOrForeign && (
        <div className="card table-container">
          <h3>Deviezen permits</h3>
          <table className="table">
            <thead>
              <tr>
                <th>No</th>
                <th>Kind</th>
                <th>Issued</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {devPermits.map((permit) => (
                <tr key={permit.id}>
                  <td>{permit.permit_no}</td>
                  <td>{permit.kind}</td>
                  <td>{permit.issued_on?.slice(0, 10) || "—"}</td>
                  <td>{permit.expires_on?.slice(0, 10) || "—"}</td>
                  <td>
                    <button type="button" onClick={() => editPermit(permit)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {devPermits.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center">
                    No permits
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <form className="form-grid" onSubmit={savePermit} style={{ marginTop: 12 }}>
            <Field label="Permit no">
              <input value={permitForm.permit_no} onChange={(e) => setPermitForm({ ...permitForm, permit_no: e.target.value })} />
            </Field>
            <Field label="Kind">
              <select value={permitForm.kind} onChange={(e) => setPermitForm({ ...permitForm, kind: e.target.value })}>
                <option value="exemption">Exemption</option>
                <option value="permit">Permit</option>
              </select>
            </Field>
            <Field label="Issued on">
              <input type="date" value={permitForm.issued_on} onChange={(e) => setPermitForm({ ...permitForm, issued_on: e.target.value })} />
            </Field>
            <Field label="Expires on">
              <input type="date" value={permitForm.expires_on} onChange={(e) => setPermitForm({ ...permitForm, expires_on: e.target.value })} />
            </Field>
            <Field label="Scope">
              <input value={permitForm.scope} onChange={(e) => setPermitForm({ ...permitForm, scope: e.target.value })} />
            </Field>
          </form>
          <div className="form-actions">
            {permitForm.id && (
              <button type="button" className="secondary" onClick={resetPermitForm}>
                Cancel
              </button>
            )}
            <button type="button" onClick={savePermit}>
              {permitForm.id ? "Save permit" : "Add permit"}
            </button>
          </div>
        </div>
      )}

      {/* Tax info (existing)… */}
      <form className="card form-section" onSubmit={saveTaxInfo}>
        <h3>Tax information</h3>
        <div className="form-grid">
          <Field label="CRIB number">
            <input value={taxInfo.crib_number} onChange={(e) => setTaxInfo({ ...taxInfo, crib_number: e.target.value })} />
          </Field>
        </div>
        <div className="form-actions">
          <button type="submit">Save</button>
        </div>
      </form>

      {/* Screenings table + add form (existing)… */}
      <div className="card table-container">
        <h3>Screenings</h3>
        <table className="table">
          <thead>
            <tr>
              <th>Kind</th>
              <th>Provider</th>
              <th>Result</th>
              <th>Checked on</th>
              <th>Reference</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {screenings.map((s) => (
              <tr key={s.id}>
                <td>{s.kind}</td>
                <td>{s.provider}</td>
                <td>{s.result}</td>
                <td>{s.checked_on?.slice(0, 10) || "—"}</td>
                <td className="cell-ellipsis">{s.reference || "—"}</td>
                <td>
                  <button type="button" onClick={() => editScreening(s)}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
            {screenings.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center">
                  No screenings
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <form
          className="form-grid"
          onSubmit={(e) => {
            e.preventDefault();
            saveScreening(e);
          }}
          style={{ marginTop: 12 }}>
          <Field label="Kind">
            <select value={screenForm.kind} onChange={(e) => setScreenForm({ ...screenForm, kind: e.target.value })}>
              <option value="pep">PEP</option>
              <option value="sanctions">Sanctions</option>
              <option value="adverse_media">Adverse media</option>
            </select>
          </Field>
          <Field label="Provider">
            <input value={screenForm.provider} onChange={(e) => setScreenForm({ ...screenForm, provider: e.target.value })} />
          </Field>
          <Field label="Result">
            <select value={screenForm.result} onChange={(e) => setScreenForm({ ...screenForm, result: e.target.value })}>
              <option value="clear">Clear</option>
              <option value="match">Match</option>
              <option value="possible_match">Possible match</option>
            </select>
          </Field>
          <Field label="Checked on">
            <input type="date" value={screenForm.checked_on} onChange={(e) => setScreenForm({ ...screenForm, checked_on: e.target.value })} />
          </Field>
          <Field label="Reference">
            <input value={screenForm.reference} onChange={(e) => setScreenForm({ ...screenForm, reference: e.target.value })} />
          </Field>
        </form>
        <div className="form-actions">
          {screenForm.id && (
            <button type="button" className="secondary" onClick={resetScreenForm}>
              Cancel
            </button>
          )}
          <button type="button" onClick={saveScreening}>
            {screenForm.id ? "Save screening" : "Add screening"}
          </button>
        </div>
      </div>
    </>
  );
};
